import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { horsesAPI, referenceAPI } from '../services/api';
import AddReferenceModal from './AddReferenceModal';
import { deriveCurrentStatusAfterSaleDateChange, ALL_HORSE_STATUSES } from '../utils/horseStatus';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';

const OWNER_TYPE_OPTIONS = [
  { value: 'owned', label: 'Owned' },
  { value: 'client', label: 'Client' },
  { value: 'leased', label: 'Leased' },
  { value: 'reference', label: 'Reference' },
];

const AddHorseModal = ({ open, onClose, onSuccess, defaultType = '' }) => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [horseTypes, setHorseTypes] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states for adding new reference values
  const [openBreedModal, setOpenBreedModal] = useState(false);
  const [openColorModal, setOpenColorModal] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    registered_name: '',
    type_id: '',
    owner_type: 'owned',
    breed_id: '',
    color_id: '',
    date_of_birth: '',
    color_panel: '',
    dna_test: '',
    disease_panel: '',
    blanket_size: '',
    date_purchased: '',
    date_purchase_paid: '',
    date_sold: '',
    date_sale_paid: '',
    purchase_amount: '',
    sale_amount: '',
    current_status: 'active',
    notes: '',
  });

  useEffect(() => {
    if (open) {
      loadReferenceData();
      // If defaultType is provided, find matching type_id
      if (defaultType && horseTypes.length > 0) {
        const matchingType = horseTypes.find(t => t.name === defaultType || String(t.id) === defaultType);
        if (matchingType) {
          setFormData(prev => ({ ...prev, type_id: String(matchingType.id) }));
        }
      }
    }
  }, [open, defaultType, horseTypes]);

  const loadReferenceData = async () => {
    try {
      const [typesRes, breedsRes, colorsRes] = await Promise.all([
        referenceAPI.getHorseTypes(),
        referenceAPI.getBreeds(),
        referenceAPI.getColors(),
      ]);
      setHorseTypes(toArray(typesRes.data, 'horse types'));
      setBreeds(toArray(breedsRes.data, 'breeds'));
      setColors(toArray(colorsRes.data, 'colors'));
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  };

  const typeOptions = useMemo(
    () => [...horseTypes].sort((a, b) => a.name.localeCompare(b.name)),
    [horseTypes]
  );

  const breedOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...[...breeds].sort((a, b) => a.name.localeCompare(b.name)).map((breed) => ({ id: String(breed.id), label: breed.name })),
  ], [breeds]);

  const colorOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...[...colors].sort((a, b) => a.name.localeCompare(b.name)).map((color) => ({ id: String(color.id), label: color.name })),
  ], [colors]);

  const currentStatusOptions = ALL_HORSE_STATUSES.filter((o) => o.value !== 'current');

  const handleClose = () => {
    setFormData({
      name: '',
      registered_name: '',
      type_id: '',
      owner_type: 'owned',
      breed_id: '',
      color_id: '',
      date_of_birth: '',
      color_panel: '',
      dna_test: '',
      disease_panel: '',
      blanket_size: '',
      date_purchased: '',
      date_purchase_paid: '',
      date_sold: '',
      date_sale_paid: '',
      purchase_amount: '',
      sale_amount: '',
      current_status: 'active',
      notes: '',
    });
    fields.reset();
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Either name satisfies the "one of the two" check, which is reported on Barn Name.
    fields.clear(name === 'registered_name' ? 'name' : name);
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'date_sale_paid' || name === 'date_sold') {
        const status = deriveCurrentStatusAfterSaleDateChange(prev, name, value);
        if (status !== undefined) next.current_status = status;
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    const trimmedName = formData.name.trim();
    const trimmedRegisteredName = formData.registered_name.trim();

    const valid = fields.validate({
      name: !trimmedName && !trimmedRegisteredName && 'Enter a barn name or a registered name',
      type_id: !formData.type_id && 'Horse type is required',
    });
    if (!valid) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        name: trimmedName,
        registered_name: trimmedRegisteredName,
        type_id: formData.type_id ? parseInt(formData.type_id) : null,
        breed_id: formData.breed_id ? parseInt(formData.breed_id) : null,
        color_id: formData.color_id ? parseInt(formData.color_id) : null,
      };
      const response = await horsesAPI.create(payload);
      if (onSuccess) {
        onSuccess(response.data);
      }
      handleClose();
    } catch (error) {
      console.error('Error saving horse:', error);
      notify.error(errorMessage(error, 'Error saving horse'));
    } finally {
      setLoading(false);
    }
  };

  // Handler for adding new breed
  const handleAddBreed = async (data) => {
    const response = await referenceAPI.createBreed(data);
    const breedsResponse = await referenceAPI.getBreeds();
    setBreeds(toArray(breedsResponse.data, 'breeds'));
    // Automatically select the newly added breed
    setFormData((prev) => ({ ...prev, breed_id: String(response.data.id) }));
  };

  // Handler for adding new color
  const handleAddColor = async (data) => {
    const response = await referenceAPI.createColor(data);
    const colorsResponse = await referenceAPI.getColors();
    setColors(toArray(colorsResponse.data, 'colors'));
    // Automatically select the newly added color
    setFormData((prev) => ({ ...prev, color_id: String(response.data.id) }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <DialogTitle>Add New Horse</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="name"
                label="Barn Name"
                value={formData.name}
                onChange={handleInputChange}
                {...fields.props('name')}
                fullWidth
                autoFocus
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="registered_name"
                label="Registered Name"
                value={formData.registered_name}
                onChange={handleInputChange}
                fullWidth
                inputProps={{ maxLength: 20 }}
                helperText="Max 20 characters (AQHA limit)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                fullWidth
                disableClearable
                options={typeOptions}
                getOptionLabel={(type) => type.name}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                filterOptions={createPrefixFilter((type) => type.name)}
                componentsProps={{ paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 } } }}
                value={typeOptions.find((type) => String(type.id) === formData.type_id) ?? null}
                onChange={(e, newValue) => {
                  fields.clear('type_id');
                  setFormData((prev) => ({ ...prev, type_id: newValue ? String(newValue.id) : '' }));
                }}
                renderInput={(params) => <TextField {...params} label="Type" required {...fields.props('type_id')} />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                fullWidth
                disableClearable
                options={OWNER_TYPE_OPTIONS}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v?.value}
                filterOptions={createPrefixFilter((o) => o.label)}
                componentsProps={{ paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 } } }}
                value={OWNER_TYPE_OPTIONS.find((o) => o.value === formData.owner_type) ?? OWNER_TYPE_OPTIONS[0]}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, owner_type: newValue?.value ?? '' }))
                }
                renderInput={(params) => <TextField {...params} label="Owner Type" />}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                fullWidth
                disableClearable
                options={currentStatusOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v?.value}
                filterOptions={createPrefixFilter((o) => o.label)}
                componentsProps={{ paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 } } }}
                value={currentStatusOptions.find((o) => o.value === formData.current_status) ?? currentStatusOptions[0]}
                onChange={(e, newValue) =>
                  setFormData((prev) => ({ ...prev, current_status: newValue?.value ?? '' }))
                }
                renderInput={(params) => <TextField {...params} label="Status" />}
              />
            </Grid>

            {/* Breed with Add Button */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Autocomplete
                  size="small"
                  fullWidth
                  disableClearable
                  options={breedOptions}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  filterOptions={createPrefixFilter((o) => o.label)}
                  componentsProps={{ paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 } } }}
                  value={breedOptions.find((o) => o.id === formData.breed_id) ?? breedOptions[0]}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, breed_id: newValue?.id ?? '' }))
                  }
                  renderInput={(params) => <TextField {...params} label="Breed" />}
                />
                <Button
                  variant="outlined"
                  onClick={() => setOpenBreedModal(true)}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <AddIcon />
                </Button>
              </Box>
            </Grid>

            {/* Color with Add Button */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Autocomplete
                  size="small"
                  fullWidth
                  disableClearable
                  options={colorOptions}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  filterOptions={createPrefixFilter((o) => o.label)}
                  componentsProps={{ paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 } } }}
                  value={colorOptions.find((o) => o.id === formData.color_id) ?? colorOptions[0]}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, color_id: newValue?.id ?? '' }))
                  }
                  renderInput={(params) => <TextField {...params} label="Color" />}
                />
                <Button
                  variant="outlined"
                  onClick={() => setOpenColorModal(true)}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <AddIcon />
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="date_of_birth"
                label="Date of Birth"
                type="date"
                value={formData.date_of_birth}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="color_panel"
                label="Color Panel"
                value={formData.color_panel}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="dna_test"
                label="DNA Test"
                value={formData.dna_test}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="disease_panel"
                label="Disease Panel"
                value={formData.disease_panel}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="blanket_size"
                label="Blanket Size"
                value={formData.blanket_size}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="purchase_amount"
                label="Purchase Amount"
                type="number"
                value={formData.purchase_amount}
                onChange={handleInputChange}
                fullWidth
                inputProps={{ step: '0.01' }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="sale_amount"
                label="Sale Amount"
                type="number"
                value={formData.sale_amount}
                onChange={handleInputChange}
                fullWidth
                inputProps={{ step: '0.01' }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="date_purchased"
                label="Date Purchased"
                type="date"
                value={formData.date_purchased}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="date_purchase_paid"
                label="Date Purchase Paid"
                type="date"
                value={formData.date_purchase_paid}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="date_sold"
                label="Date Sold"
                type="date"
                value={formData.date_sold}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                  size="small"
                name="date_sale_paid"
                label="Date Sale Paid"
                type="date"
                value={formData.date_sale_paid}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                  size="small"
                name="notes"
                label="Notes"
                value={formData.notes}
                onChange={handleInputChange}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Adding...' : 'Add Horse'}
        </Button>
      </DialogActions>

      {/* Nested modals for adding reference data */}
      <AddReferenceModal
        open={openBreedModal}
        onClose={() => setOpenBreedModal(false)}
        onSave={handleAddBreed}
        title="Breed"
        label="Breed Name"
      />

      <AddReferenceModal
        open={openColorModal}
        onClose={() => setOpenColorModal(false)}
        onSave={handleAddColor}
        title="Color"
        label="Color Name"
      />
    </Dialog>
  );
};

export default AddHorseModal;

